import { useState, type DragEvent } from "react";
import {
  Bot,
  GitFork,
  Hand,
  CircleDot,
  Square,
  Database,
  Mail,
  Bell,
  Clock,
  Shield,
  ArrowRightLeft,
  Globe,
  CreditCard,
  Zap,
  Calendar,
  Stethoscope,
  FileText,
  Phone,
  Printer,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { cn } from "../../lib/utils";

export interface WfPaletteItem {
  type: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

interface WfPaletteCategory {
  label: string;
  items: WfPaletteItem[];
}

const WF_PALETTE_CATEGORIES: WfPaletteCategory[] = [
  {
    label: "TRIGGERS",
    items: [
      { type: "wf_trigger_webhook", label: "Webhook", icon: <Zap className="h-3.5 w-3.5" />, color: "text-blue-500" },
      { type: "wf_trigger_schedule", label: "Scheduled", icon: <Calendar className="h-3.5 w-3.5" />, color: "text-violet-500" },
      { type: "wf_trigger_clinical", label: "Clinical Event", icon: <Stethoscope className="h-3.5 w-3.5" />, color: "text-emerald-500" },
      { type: "wf_trigger_claims", label: "Claims Event", icon: <CreditCard className="h-3.5 w-3.5" />, color: "text-blue-500" },
      { type: "wf_trigger_referral", label: "Referral Event", icon: <FileText className="h-3.5 w-3.5" />, color: "text-cyan-500" },
      { type: "wf_trigger_manual", label: "Manual Start", icon: <Hand className="h-3.5 w-3.5" />, color: "text-zinc-500" },
    ],
  },
  {
    label: "AI AGENTS",
    items: [
      { type: "agent_ref", label: "Agent (from Studio)", icon: <Bot className="h-3.5 w-3.5" />, color: "text-violet-500" },
    ],
  },
  {
    label: "HEALTHCARE",
    items: [
      { type: "ehr_action", label: "EHR Read/Write", icon: <Database className="h-3.5 w-3.5" />, color: "text-emerald-500" },
      { type: "payer_action", label: "Payer Action", icon: <CreditCard className="h-3.5 w-3.5" />, color: "text-blue-500" },
      { type: "compliance", label: "Compliance Check", icon: <Shield className="h-3.5 w-3.5" />, color: "text-red-500" },
    ],
  },
  {
    label: "COMMUNICATION",
    items: [
      { type: "notify_email", label: "Send Email", icon: <Mail className="h-3.5 w-3.5" />, color: "text-orange-500" },
      { type: "notify_sms", label: "Send SMS", icon: <Phone className="h-3.5 w-3.5" />, color: "text-blue-500" },
      { type: "notify_fax", label: "Send Fax", icon: <Printer className="h-3.5 w-3.5" />, color: "text-zinc-500" },
      { type: "notify_alert", label: "Staff Alert", icon: <Bell className="h-3.5 w-3.5" />, color: "text-yellow-500" },
    ],
  },
  {
    label: "FLOW CONTROL",
    items: [
      { type: "wf_start", label: "Start", icon: <CircleDot className="h-3.5 w-3.5" />, color: "text-emerald-500" },
      { type: "wf_end", label: "End", icon: <Square className="h-3.5 w-3.5" />, color: "text-zinc-500" },
      { type: "decision", label: "Decision", icon: <GitFork className="h-3.5 w-3.5" />, color: "text-amber-500" },
      { type: "human_review", label: "Human Review", icon: <Hand className="h-3.5 w-3.5" />, color: "text-rose-500" },
      { type: "timer", label: "Timer / Wait", icon: <Clock className="h-3.5 w-3.5" />, color: "text-purple-500" },
      { type: "data_transform", label: "Data Transform", icon: <ArrowRightLeft className="h-3.5 w-3.5" />, color: "text-cyan-500" },
      { type: "api_call", label: "API Call", icon: <Globe className="h-3.5 w-3.5" />, color: "text-indigo-500" },
    ],
  },
];

function getDefaultWfData(type: string, label: string): Record<string, unknown> {
  if (type === "agent_ref") return { name: "Agent Step", agentId: "", agentName: "" };
  if (type === "decision") return { name: label, conditions: [], defaultBranch: "else" };
  if (type === "ehr_action") return { name: label, ehrAction: "read", resourceType: "Patient", query: "" };
  if (type === "payer_action") return { name: label, payerAction: "eligibility", payerId: "" };
  if (type === "compliance") return { name: label, checkType: "hipaa_audit", failAction: "block" };
  if (type.startsWith("notify_")) {
    const channel = type.replace("notify_", "");
    return { name: label, channel, recipient: "", subject: "", body: "" };
  }
  if (type === "human_review") return { name: label, reviewerRole: "clinician", requireReason: true, timeoutMinutes: 60 };
  if (type === "timer") return { name: label, delayType: "fixed", delayMinutes: 5, delayDescription: "5 minutes" };
  if (type === "data_transform") return { name: label, transformType: "fhir_to_internal", mappingExpression: "" };
  if (type === "api_call") return { name: label, method: "POST", url: "", headers: {}, body: "" };
  if (type.startsWith("wf_trigger_")) {
    const triggerType = type.replace("wf_trigger_", "");
    return { name: label, triggerType, filters: {} };
  }
  return {};
}

function onDragStart(event: DragEvent, item: WfPaletteItem) {
  const nodeType = item.type.startsWith("wf_trigger_") ? "wf_trigger" : item.type.startsWith("notify_") ? "notification" : item.type;
  const data = getDefaultWfData(item.type, item.label);
  event.dataTransfer.setData("application/reactflow-type", nodeType);
  event.dataTransfer.setData("application/reactflow-data", JSON.stringify(data));
  event.dataTransfer.effectAllowed = "move";
}

function CategorySection({ category }: { category: WfPaletteCategory }) {
  const [open, setOpen] = useState(true);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} />
        {category.label}
        <span className="ml-auto text-[9px] font-normal tabular-nums">{category.items.length}</span>
      </button>
      {open && (
        <div className="space-y-0.5 ml-1">
          {category.items.map((item, i) => (
            <div
              key={`${item.type}-${i}`}
              draggable
              onDragStart={(e) => onDragStart(e, item)}
              className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs cursor-grab active:cursor-grabbing hover:bg-accent/50 transition-colors"
            >
              <span className={item.color}>{item.icon}</span>
              <span className="text-foreground/80">{item.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface WorkflowPaletteProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function WorkflowPalette({ collapsed, onToggle }: WorkflowPaletteProps) {
  if (collapsed) {
    return (
      <div className="w-10 shrink-0 border-r border-border bg-card/50 flex flex-col items-center pt-2">
        <button
          type="button"
          onClick={onToggle}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Expand palette"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-56 shrink-0 border-r border-border bg-card/50 flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Process Nodes</span>
        <button
          type="button"
          onClick={onToggle}
          className="p-1 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Collapse palette"
        >
          <PanelLeftClose className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-auto-hide px-2.5 py-2 space-y-3">
        {WF_PALETTE_CATEGORIES.map((cat) => (
          <CategorySection key={cat.label} category={cat} />
        ))}
      </div>
    </div>
  );
}

export { WF_PALETTE_CATEGORIES, getDefaultWfData };
